import DataUsageIcon from '@mui/icons-material/DataUsage'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import SearchIcon from '@mui/icons-material/Search'
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import {
  useClickUpSpaces,
  usePointsOverview,
  useClientUsage,
  type PointsOverview,
  type PointsOverviewClient,
} from 'api/aws/strategy-builder/useStrategyBuilder'
import { useLayoutContext } from 'context/LayoutProvider/LayoutProvider'
import React, { useCallback, useMemo, useState } from 'react'

// ── Types ─────────────────────────────────────────────────────────────────────

interface ServiceLineAllocation {
  line: string
  allocated: number
  used: number
}

interface ClientPointsSummary {
  folderId: string
  clientName: string
  prefix: string
  serviceLines: string[]
  pointsAllocated: number
  pointsUsed: number
  pointsRemaining: number
  utilizationPercent: number
  perServiceLine: ServiceLineAllocation[]
}

interface PointsDashboardData {
  totalAllocated: number
  totalUsed: number
  totalRemaining: number
  utilizationPercent: number
  clients: ClientPointsSummary[]
  serviceLineSummary: ServiceLineAllocation[]
}

// ── Sample Data ───────────────────────────────────────────────────────────────

const SERVICE_LINES = ['SEO', 'PPC', 'Web', 'Social', 'Creative', 'Email', 'Analytics', 'UX', 'Integration']

const SERVICE_LINE_COLORS: Record<string, string> = {
  SEO: '#4caf50',
  PPC: '#2196f3',
  Web: '#ff9800',
  Social: '#e91e63',
  Creative: '#9c27b0',
  Email: '#00bcd4',
  Analytics: '#ff5722',
  UX: '#8bc34a',
  Integration: '#607d8b',
}

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

const generateSampleData = (monthMultiplier = 1): PointsDashboardData => {
  const baseClients: Omit<ClientPointsSummary, 'pointsRemaining' | 'utilizationPercent'>[] = [
    {
      folderId: 'f1',
      clientName: 'Acme Corporation',
      prefix: 'ACM',
      serviceLines: ['SEO', 'PPC', 'Web'],
      pointsAllocated: 120,
      pointsUsed: 85,
      perServiceLine: [
        { line: 'SEO', allocated: 50, used: 38 },
        { line: 'PPC', allocated: 40, used: 32 },
        { line: 'Web', allocated: 30, used: 15 },
      ],
    },
    {
      folderId: 'f2',
      clientName: 'Beta Industries',
      prefix: 'BET',
      serviceLines: ['SEO', 'Social', 'Email'],
      pointsAllocated: 90,
      pointsUsed: 82,
      perServiceLine: [
        { line: 'SEO', allocated: 40, used: 38 },
        { line: 'Social', allocated: 30, used: 28 },
        { line: 'Email', allocated: 20, used: 16 },
      ],
    },
    {
      folderId: 'f3',
      clientName: 'Gamma Solutions',
      prefix: 'GAM',
      serviceLines: ['PPC', 'Analytics', 'Creative'],
      pointsAllocated: 150,
      pointsUsed: 162,
      perServiceLine: [
        { line: 'PPC', allocated: 60, used: 68 },
        { line: 'Analytics', allocated: 50, used: 52 },
        { line: 'Creative', allocated: 40, used: 42 },
      ],
    },
    {
      folderId: 'f4',
      clientName: 'Delta Tech',
      prefix: 'DEL',
      serviceLines: ['Web', 'UX', 'Integration'],
      pointsAllocated: 200,
      pointsUsed: 110,
      perServiceLine: [
        { line: 'Web', allocated: 80, used: 45 },
        { line: 'UX', allocated: 60, used: 35 },
        { line: 'Integration', allocated: 60, used: 30 },
      ],
    },
    {
      folderId: 'f5',
      clientName: 'Epsilon Media',
      prefix: 'EPS',
      serviceLines: ['Social', 'Creative', 'Email', 'PPC'],
      pointsAllocated: 180,
      pointsUsed: 145,
      perServiceLine: [
        { line: 'Social', allocated: 50, used: 42 },
        { line: 'Creative', allocated: 50, used: 40 },
        { line: 'Email', allocated: 40, used: 35 },
        { line: 'PPC', allocated: 40, used: 28 },
      ],
    },
    {
      folderId: 'f6',
      clientName: 'Zeta Consulting',
      prefix: 'ZET',
      serviceLines: ['SEO', 'Analytics', 'Web'],
      pointsAllocated: 95,
      pointsUsed: 72,
      perServiceLine: [
        { line: 'SEO', allocated: 35, used: 28 },
        { line: 'Analytics', allocated: 30, used: 24 },
        { line: 'Web', allocated: 30, used: 20 },
      ],
    },
    {
      folderId: 'f7',
      clientName: 'Theta Manufacturing',
      prefix: 'THE',
      serviceLines: ['PPC', 'Email'],
      pointsAllocated: 60,
      pointsUsed: 58,
      perServiceLine: [
        { line: 'PPC', allocated: 35, used: 34 },
        { line: 'Email', allocated: 25, used: 24 },
      ],
    },
    {
      folderId: 'f8',
      clientName: 'Iota Healthcare',
      prefix: 'IOT',
      serviceLines: ['SEO', 'Creative', 'UX', 'Web'],
      pointsAllocated: 220,
      pointsUsed: 145,
      perServiceLine: [
        { line: 'SEO', allocated: 60, used: 42 },
        { line: 'Creative', allocated: 60, used: 40 },
        { line: 'UX', allocated: 50, used: 35 },
        { line: 'Web', allocated: 50, used: 28 },
      ],
    },
    {
      folderId: 'f9',
      clientName: 'Kappa Financial',
      prefix: 'KAP',
      serviceLines: ['PPC', 'Analytics', 'Integration'],
      pointsAllocated: 175,
      pointsUsed: 190,
      perServiceLine: [
        { line: 'PPC', allocated: 75, used: 85 },
        { line: 'Analytics', allocated: 50, used: 55 },
        { line: 'Integration', allocated: 50, used: 50 },
      ],
    },
    {
      folderId: 'f10',
      clientName: 'Lambda Retail',
      prefix: 'LAM',
      serviceLines: ['SEO', 'PPC', 'Social', 'Email'],
      pointsAllocated: 160,
      pointsUsed: 120,
      perServiceLine: [
        { line: 'SEO', allocated: 45, used: 35 },
        { line: 'PPC', allocated: 45, used: 32 },
        { line: 'Social', allocated: 35, used: 28 },
        { line: 'Email', allocated: 35, used: 25 },
      ],
    },
    {
      folderId: 'f11',
      clientName: 'Mu Logistics',
      prefix: 'MUL',
      serviceLines: ['Web', 'Integration'],
      pointsAllocated: 80,
      pointsUsed: 35,
      perServiceLine: [
        { line: 'Web', allocated: 50, used: 20 },
        { line: 'Integration', allocated: 30, used: 15 },
      ],
    },
    {
      folderId: 'f12',
      clientName: 'Nu Education',
      prefix: 'NUE',
      serviceLines: ['SEO', 'Creative', 'Social'],
      pointsAllocated: 110,
      pointsUsed: 98,
      perServiceLine: [
        { line: 'SEO', allocated: 40, used: 38 },
        { line: 'Creative', allocated: 40, used: 35 },
        { line: 'Social', allocated: 30, used: 25 },
      ],
    },
  ]

  // Apply month multiplier to simulate different months having different usage
  const clients: ClientPointsSummary[] = baseClients.map(c => {
    const usedAdj = Math.round(c.pointsUsed * monthMultiplier)
    const remaining = c.pointsAllocated - usedAdj
    return {
      ...c,
      pointsUsed: usedAdj,
      pointsRemaining: remaining,
      utilizationPercent: Math.round((usedAdj / c.pointsAllocated) * 1000) / 10,
      perServiceLine: c.perServiceLine.map(sl => ({
        ...sl,
        used: Math.round(sl.used * monthMultiplier),
      })),
    }
  })

  const totalAllocated = clients.reduce((sum, c) => sum + c.pointsAllocated, 0)
  const totalUsed = clients.reduce((sum, c) => sum + c.pointsUsed, 0)

  // Build service line summary by aggregating across clients
  const slMap = new Map<string, { allocated: number; used: number }>()
  for (const client of clients) {
    for (const sl of client.perServiceLine) {
      const existing = slMap.get(sl.line) ?? { allocated: 0, used: 0 }
      slMap.set(sl.line, {
        allocated: existing.allocated + sl.allocated,
        used: existing.used + sl.used,
      })
    }
  }
  const serviceLineSummary = SERVICE_LINES.filter(l => slMap.has(l)).map(l => ({
    line: l,
    allocated: slMap.get(l)!.allocated,
    used: slMap.get(l)!.used,
  }))

  return {
    totalAllocated,
    totalUsed,
    totalRemaining: totalAllocated - totalUsed,
    utilizationPercent: totalAllocated > 0 ? Math.round((totalUsed / totalAllocated) * 1000) / 10 : 0,
    clients,
    serviceLineSummary,
  }
}

// ── Hook stub ─────────────────────────────────────────────────────────────────

// Real data hook — converts PointsOverview API data to the dashboard format
const useRealPointsDashboard = (): { data: PointsDashboardData; isLoading: boolean } => {
  const overviewQuery = usePointsOverview()

  const data: PointsDashboardData = useMemo(() => {
    if (!overviewQuery.data) {
      return { totalAllocated: 0, totalUsed: 0, totalRemaining: 0, utilizationPercent: 0, clients: [], serviceLineSummary: [] }
    }

    const overview = overviewQuery.data
    const clients: ClientPointsSummary[] = overview.clients.map(c => ({
      folderId: c.clientName.replace(/\s+/g, '_').toLowerCase(),
      clientName: c.clientName,
      prefix: c.clientName.substring(0, 3).toUpperCase(),
      serviceLines: c.serviceLines,
      pointsAllocated: c.totalPoints,
      pointsUsed: 0, // Will be filled by per-client ClickUp fetch
      pointsRemaining: c.totalPoints,
      utilizationPercent: 0,
      perServiceLine: c.perServiceLine.map(sl => ({ line: sl.line, allocated: sl.allocated, used: 0 })),
    }))

    const serviceLineSummary = overview.serviceLineSummary.map(sl => ({
      line: sl.line,
      allocated: sl.allocated,
      used: 0,
    }))

    return {
      totalAllocated: overview.totalAllocated,
      totalUsed: 0,
      totalRemaining: overview.totalAllocated,
      utilizationPercent: 0,
      clients,
      serviceLineSummary,
    }
  }, [overviewQuery.data])

  return { data, isLoading: overviewQuery.isLoading }
}

// ── Utility ───────────────────────────────────────────────────────────────────

const getUtilizationColor = (percent: number): 'success' | 'warning' | 'error' => {
  if (percent > 100) return 'error'
  if (percent >= 80) return 'warning'
  return 'success'
}

const getUtilizationHex = (percent: number): string => {
  if (percent > 100) return '#f44336'
  if (percent >= 80) return '#ff9800'
  return '#4caf50'
}

// ── Summary Card ──────────────────────────────────────────────────────────────

interface SummaryCardProps {
  title: string
  value: string | number
  subtitle?: string
  circularValue?: number
}

const SummaryCard = ({ title, value, subtitle, circularValue }: SummaryCardProps) => {
  const theme = useTheme()
  return (
    <Card
      elevation={0}
      sx={{
        bgcolor: 'background.paper',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        height: '100%',
      }}
    >
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}
        >
          {title}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {value}
          </Typography>
          {circularValue !== undefined && (
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
              <CircularProgress
                variant="determinate"
                value={Math.min(circularValue, 100)}
                size={52}
                thickness={4}
                color={getUtilizationColor(circularValue)}
                sx={{ '& .MuiCircularProgress-circle': { strokeLinecap: 'round' } }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  bottom: 0,
                  right: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.65rem' }}>
                  {circularValue}%
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
        {subtitle && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  )
}

// ── Sort helpers ──────────────────────────────────────────────────────────────

type SortField = 'clientName' | 'pointsAllocated' | 'pointsUsed' | 'pointsRemaining' | 'utilizationPercent'
type SortDirection = 'asc' | 'desc'

// ── Main Component ────────────────────────────────────────────────────────────

const PointsDashboard = () => {
  const theme = useTheme()
  const { pinned } = useLayoutContext()
  const spacesQuery = useClickUpSpaces()
  const spaces = spacesQuery.data ?? []

  // Filters
  const [selectedSpaceId, setSelectedSpaceId] = useState<string | null>(null)
  const [selectedMonth, setSelectedMonth] = useState<string>(MONTHS[new Date().getMonth()])
  const [searchFilter, setSearchFilter] = useState('')

  // Sort
  const [sortField, setSortField] = useState<SortField>('clientName')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')

  // Expanded rows
  const [expandedClients, setExpandedClients] = useState<Set<string>>(new Set())

  // Data
  const { data, isLoading: pointsLoading } = useRealPointsDashboard()

  const handleSort = useCallback(
    (field: SortField) => {
      setSortDirection(prev => (sortField === field ? (prev === 'asc' ? 'desc' : 'asc') : 'asc'))
      setSortField(field)
    },
    [sortField]
  )

  const toggleExpand = useCallback((folderId: string) => {
    setExpandedClients(prev => {
      const next = new Set(prev)
      if (next.has(folderId)) next.delete(folderId)
      else next.add(folderId)
      return next
    })
  }, [])

  // Filter and sort clients
  const filteredClients = useMemo(() => {
    let clients = data.clients

    // Search filter
    if (searchFilter) {
      const lower = searchFilter.toLowerCase()
      clients = clients.filter(
        c => c.clientName.toLowerCase().includes(lower) || c.prefix.toLowerCase().includes(lower)
      )
    }

    // Service line filter via selected space (repurposed: if a space maps to a service line)
    // For now, space filter is cosmetic — all data comes from DynamoDB strategies

    return [...clients].sort((a, b) => {
      const aVal = a[sortField]
      const bVal = b[sortField]
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal)
      }
      const aNum = aVal as number
      const bNum = bVal as number
      return sortDirection === 'asc' ? aNum - bNum : bNum - aNum
    })
  }, [data.clients, searchFilter, sortField, sortDirection])

  // Recalculate summary based on filtered clients
  const filteredSummary = useMemo(() => {
    const totalAllocated = filteredClients.reduce((s, c) => s + c.pointsAllocated, 0)
    const totalUsed = filteredClients.reduce((s, c) => s + c.pointsUsed, 0)
    const slMap = new Map<string, { allocated: number; used: number }>()
    for (const client of filteredClients) {
      for (const sl of client.perServiceLine) {
        const existing = slMap.get(sl.line) ?? { allocated: 0, used: 0 }
        slMap.set(sl.line, { allocated: existing.allocated + sl.allocated, used: existing.used + sl.used })
      }
    }
    return {
      totalAllocated,
      totalUsed,
      totalRemaining: totalAllocated - totalUsed,
      utilizationPercent: totalAllocated > 0 ? Math.round((totalUsed / totalAllocated) * 1000) / 10 : 0,
      serviceLineSummary: SERVICE_LINES.filter(l => slMap.has(l)).map(l => ({
        line: l, allocated: slMap.get(l)!.allocated, used: slMap.get(l)!.used,
      })),
    }
  }, [filteredClients])

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'auto', p: 3, paddingTop: pinned ? '60px' : 0, transition: 'padding-top 0.3s ease' }}>
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          mb: 3,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
            <DataUsageIcon sx={{ fontSize: 28, color: 'rgb(102, 187, 106)' }} />
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Points Dashboard
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Track points allocation and budget utilization across clients and service lines
          </Typography>
        </Box>

        <Stack direction="row" spacing={2} alignItems="center">
          {/* Space selector */}
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>ClickUp Space</InputLabel>
            <Select
              value={selectedSpaceId ?? ''}
              label="ClickUp Space"
              onChange={e => setSelectedSpaceId(e.target.value || null)}
              onOpen={() => {
                if (spaces.length === 0) spacesQuery.refetch()
              }}
            >
              <MenuItem value="">
                <em>All Spaces</em>
              </MenuItem>
              {spaces.map(s => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Month selector */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Month</InputLabel>
            <Select value={selectedMonth} label="Month" onChange={e => setSelectedMonth(e.target.value)}>
              {MONTHS.map(m => (
                <MenuItem key={m} value={m}>
                  {m}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Box>

      {/* ── Summary Cards ───────────────────────────────────────────────── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Total Allocated"
            value={filteredSummary.totalAllocated.toLocaleString()}
            subtitle="Points across all clients"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard title="Total Used" value={filteredSummary.totalUsed.toLocaleString()} subtitle="Completed task points" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Points Remaining"
            value={filteredSummary.totalRemaining.toLocaleString()}
            subtitle={filteredSummary.totalRemaining < 0 ? 'Over budget' : 'Available balance'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Budget Utilization"
            value={`${filteredSummary.utilizationPercent}%`}
            circularValue={filteredSummary.utilizationPercent}
          />
        </Grid>
      </Grid>

      {/* ── Client Points Table ─────────────────────────────────────────── */}
      <Grid container>
        <Grid item xs={7}>
          <Paper
            elevation={0}
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              mb: 3,
              overflow: 'hidden',
            }}
          >
            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Client Points Breakdown
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {filteredClients.length} client{filteredClients.length !== 1 ? 's' : ''}
                </Typography>
              </Box>
              <TextField
                size="small"
                placeholder="Search clients..."
                value={searchFilter}
                onChange={e => setSearchFilter(e.target.value)}
                sx={{ width: 260 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <TableContainer sx={{ maxHeight: 500 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow
                    sx={{
                      '& .MuiTableCell-head': {
                        fontWeight: 700,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        color: 'text.secondary',
                        bgcolor: 'background.paper',
                      },
                    }}
                  >
                    <TableCell>
                      <TableSortLabel
                        active={sortField === 'clientName'}
                        direction={sortField === 'clientName' ? sortDirection : 'asc'}
                        onClick={() => handleSort('clientName')}
                      >
                        Client
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Service Lines</TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={sortField === 'pointsAllocated'}
                        direction={sortField === 'pointsAllocated' ? sortDirection : 'asc'}
                        onClick={() => handleSort('pointsAllocated')}
                      >
                        Allocated
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={sortField === 'pointsUsed'}
                        direction={sortField === 'pointsUsed' ? sortDirection : 'asc'}
                        onClick={() => handleSort('pointsUsed')}
                      >
                        Used
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={sortField === 'pointsRemaining'}
                        direction={sortField === 'pointsRemaining' ? sortDirection : 'asc'}
                        onClick={() => handleSort('pointsRemaining')}
                      >
                        Remaining
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ minWidth: 160 }}>
                      <TableSortLabel
                        active={sortField === 'utilizationPercent'}
                        direction={sortField === 'utilizationPercent' ? sortDirection : 'asc'}
                        onClick={() => handleSort('utilizationPercent')}
                      >
                        Utilization
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredClients.map(client => {
                    const color = getUtilizationColor(client.utilizationPercent)
                    const isExpanded = expandedClients.has(client.folderId)
                    return (
                      <React.Fragment key={client.folderId}>
                        <TableRow
                          hover
                          onClick={() => toggleExpand(client.folderId)}
                          sx={{
                            cursor: 'pointer',
                            '& > .MuiTableCell-root': { borderBottom: isExpanded ? 'none' : undefined },
                          }}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <ExpandMoreIcon
                                sx={{
                                  fontSize: 18,
                                  color: 'text.secondary',
                                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                  transition: 'transform 0.2s',
                                }}
                              />
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {client.clientName}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {client.prefix}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              {client.serviceLines.map(sl => (
                                <Chip
                                  key={sl}
                                  label={sl}
                                  size="small"
                                  sx={{
                                    height: 20,
                                    fontSize: '0.65rem',
                                    fontWeight: 600,
                                    bgcolor: `${SERVICE_LINE_COLORS[sl] ?? '#999'}20`,
                                    color: SERVICE_LINE_COLORS[sl] ?? '#999',
                                    border: `1px solid ${SERVICE_LINE_COLORS[sl] ?? '#999'}40`,
                                  }}
                                />
                              ))}
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {client.pointsAllocated}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">{client.pointsUsed}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                color: client.pointsRemaining < 0 ? 'error.main' : 'text.primary',
                              }}
                            >
                              {client.pointsRemaining}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <LinearProgress
                                variant="determinate"
                                value={Math.min(client.utilizationPercent, 100)}
                                color={color}
                                sx={{
                                  flex: 1,
                                  height: 6,
                                  borderRadius: 3,
                                  bgcolor: `${getUtilizationHex(client.utilizationPercent)}15`,
                                }}
                              />
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: 700,
                                  minWidth: 42,
                                  textAlign: 'right',
                                  color: getUtilizationHex(client.utilizationPercent),
                                }}
                              >
                                {client.utilizationPercent}%
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>

                        {/* Expanded per-service-line breakdown */}
                        {isExpanded && (
                          <TableRow>
                            <TableCell
                              colSpan={6}
                              sx={{
                                py: 0,
                                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
                              }}
                            >
                              <Box sx={{ p: 2 }}>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ fontWeight: 700, mb: 1, display: 'block', textTransform: 'uppercase' }}
                                >
                                  Service Line Breakdown
                                </Typography>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow
                                      sx={{
                                        '& .MuiTableCell-head': {
                                          fontWeight: 600,
                                          fontSize: '0.7rem',
                                          color: 'text.secondary',
                                          py: 0.5,
                                        },
                                      }}
                                    >
                                      <TableCell>Service Line</TableCell>
                                      <TableCell align="right">Allocated</TableCell>
                                      <TableCell align="right">Used</TableCell>
                                      <TableCell align="right">Remaining</TableCell>
                                      <TableCell sx={{ minWidth: 120 }}>Utilization</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {client.perServiceLine.map(sl => {
                                      const slRemaining = sl.allocated - sl.used
                                      const slUtil =
                                        sl.allocated > 0 ? Math.round((sl.used / sl.allocated) * 1000) / 10 : 0
                                      return (
                                        <TableRow key={sl.line} sx={{ '&:last-child td': { borderBottom: 'none' } }}>
                                          <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                              <Box
                                                sx={{
                                                  width: 8,
                                                  height: 8,
                                                  borderRadius: '50%',
                                                  bgcolor: SERVICE_LINE_COLORS[sl.line] ?? '#999',
                                                }}
                                              />
                                              <Typography variant="body2">{sl.line}</Typography>
                                            </Box>
                                          </TableCell>
                                          <TableCell align="right">
                                            <Typography variant="body2">{sl.allocated}</Typography>
                                          </TableCell>
                                          <TableCell align="right">
                                            <Typography variant="body2">{sl.used}</Typography>
                                          </TableCell>
                                          <TableCell align="right">
                                            <Typography
                                              variant="body2"
                                              sx={{ color: slRemaining < 0 ? 'error.main' : 'text.primary' }}
                                            >
                                              {slRemaining}
                                            </Typography>
                                          </TableCell>
                                          <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                              <LinearProgress
                                                variant="determinate"
                                                value={Math.min(slUtil, 100)}
                                                color={getUtilizationColor(slUtil)}
                                                sx={{ flex: 1, height: 4, borderRadius: 2 }}
                                              />
                                              <Typography
                                                variant="caption"
                                                sx={{ minWidth: 36, textAlign: 'right', fontWeight: 600 }}
                                              >
                                                {slUtil}%
                                              </Typography>
                                            </Box>
                                          </TableCell>
                                        </TableRow>
                                      )
                                    })}
                                  </TableBody>
                                </Table>
                              </Box>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    )
                  })}
                  {filteredClients.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No clients match the current filter
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
        <Grid item xs={5}>
          {/* ── Service Line Summary ────────────────────────────────────────── */}
          <Paper
            elevation={0}
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              p: 2.5,
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
              Service Line Summary
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Stack spacing={1.5}>
              {filteredSummary.serviceLineSummary.map(sl => {
                const util = sl.allocated > 0 ? Math.round((sl.used / sl.allocated) * 1000) / 10 : 0
                const barColor = SERVICE_LINE_COLORS[sl.line] ?? '#999'
                return (
                  <Box key={sl.line} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ minWidth: 100, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: barColor, flexShrink: 0 }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {sl.line}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(util, 100)}
                        sx={{
                          height: 12,
                          borderRadius: 6,
                          bgcolor: `${barColor}15`,
                          '& .MuiLinearProgress-bar': {
                            bgcolor: barColor,
                            borderRadius: 6,
                          },
                        }}
                      />
                    </Box>
                    <Box
                      sx={{ minWidth: 140, display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {sl.used} / {sl.allocated}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 700, color: getUtilizationHex(util), minWidth: 40, textAlign: 'right' }}
                      >
                        {util}%
                      </Typography>
                    </Box>
                  </Box>
                )
              })}
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  )
}

export default PointsDashboard
